import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Moon, Sun, Contrast, Maximize, Minimize } from "lucide-react";
import { isWebMode } from "../../utils/environmentDetector";
import { useFullscreen } from "../contexts/FullscreenContext";
import useTranslation from "../hooks/useTranslation";
import {
  applyColorMode,
  COLOR_MODE_EVENT,
  isDayOnlyPath,
  readColorMode,
  readHighContrast,
  writeHighContrast,
  writeSessionColorMode,
} from "../../utils/colorMode";

export const ColorModeSync = () => {
  const location = useLocation();

  useEffect(() => {
    const apply = () => applyColorMode(readColorMode(), location.pathname);
    apply();
    window.addEventListener(COLOR_MODE_EVENT, apply);
    return () => window.removeEventListener(COLOR_MODE_EVENT, apply);
  }, [location.pathname]);

  return null;
};

const AppearanceControls = () => {
  const translation = useTranslation();
  const location = useLocation();
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const [mode, setMode] = useState(readColorMode);
  const [contrast, setContrast] = useState(readHighContrast);
  const onDayOnly = isDayOnlyPath(location.pathname);
  const dark = mode === "dark";

  useEffect(() => {
    const onChange = (event) => {
      setMode(event.detail === "dark" ? "dark" : "light");
      setContrast(readHighContrast());
    };
    window.addEventListener(COLOR_MODE_EVENT, onChange);
    return () => window.removeEventListener(COLOR_MODE_EVENT, onChange);
  }, []);

  const toggleMode = () => {
    const next = dark ? "light" : "dark";
    setMode(next);
    writeSessionColorMode(next);
    applyColorMode(next, location.pathname);
  };

  const toggleContrast = () => {
    const next = !contrast;
    setContrast(next);
    writeHighContrast(next);
    applyColorMode(mode, location.pathname);
  };

  return (
    <>
      {!onDayOnly && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={toggleMode}
          aria-label={
            dark
              ? translation.switchToLightMode || "Light mode"
              : translation.switchToDarkMode || "Dark mode"
          }
          title={
            dark
              ? translation.switchToLightMode || "Light mode"
              : translation.switchToDarkMode || "Dark mode"
          }
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
      )}
      {!onDayOnly && dark && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`h-8 w-8 ${contrast ? "bg-secondary text-foreground" : ""}`}
          onClick={toggleContrast}
          aria-pressed={contrast}
          aria-label={
            contrast
              ? translation.highContrastOff || "Standard contrast"
              : translation.highContrast || "High contrast"
          }
          title={
            contrast
              ? translation.highContrastOff || "Standard contrast"
              : translation.highContrast || "High contrast"
          }
        >
          <Contrast className="!h-[17px] !w-[17px]" />
        </Button>
      )}
      {!isWebMode() && (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={toggleFullscreen}
        aria-label={
          isFullscreen
            ? translation.exitFullscreen || "Exit fullscreen"
            : translation.enterFullscreen || "Enter fullscreen"
        }
        title={
          isFullscreen
            ? translation.exitFullscreen || "Exit fullscreen"
            : translation.enterFullscreen || "Enter fullscreen"
        }
      >
        {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
      </Button>
      )}
    </>
  );
};

export default AppearanceControls;
